import React, { useState } from 'react';
import '@/ui/tokens.css';
import {
  Button,
  IconButton,
  Card,
  CardHeader,
  Badge,
  Input,
  Textarea,
  Select,
  SearchInput,
  Tabs,
  Divider,
  Skeleton,
  EmptyState,
  Modal,
  Drawer,
  ToastContainer,
  Toast,
  DataTable,
  DataTableColumn,
  FormGrid,
  FormGridItem,
  FormSection,
  ListRow,
} from '@/ui/components';
import './Styleguide.css';

export const Styleguide: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = (type: Toast['type']) => {
    const newToast: Toast = {
      id: Date.now().toString(),
      type,
      title: `${type.charAt(0).toUpperCase() + type.slice(1)} notification`,
      description: 'This is a sample notification message.',
    };
    setToasts((prev) => [...prev, newToast]);
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const tableColumns: DataTableColumn[] = [
    { key: 'name', label: 'Name', width: '200px' },
    { key: 'email', label: 'Email', width: '250px' },
    { key: 'role', label: 'Role', width: '150px' },
    {
      key: 'status',
      label: 'Status',
      render: (value) => <Badge status={value}>{value}</Badge>,
    },
  ];

  const tableData = [
    { name: 'John Doe', email: 'john@example.com', role: 'Engineer', status: 'paid' },
    { name: 'Jane Smith', email: 'jane@example.com', role: 'Manager', status: 'sent' },
    { name: 'Bob Johnson', email: 'bob@example.com', role: 'Designer', status: 'draft' },
  ];

  return (
    <div className="styleguide">
      <div className="styleguide__header">
        <h1 className="styleguide__title">UI Component Library</h1>
        <p className="styleguide__subtitle">Data Grid Focus · Structured Operations</p>
      </div>

      {/* Colors */}
      <section className="styleguide__section">
        <h2 className="styleguide__section-title">Colors</h2>
        <div className="styleguide__color-grid">
          <div className="styleguide__color-item">
            <div className="styleguide__color-swatch" style={{ backgroundColor: 'var(--color-primary)' }} />
            <span>Primary</span>
          </div>
          <div className="styleguide__color-item">
            <div className="styleguide__color-swatch" style={{ backgroundColor: 'var(--color-success)' }} />
            <span>Success</span>
          </div>
          <div className="styleguide__color-item">
            <div className="styleguide__color-swatch" style={{ backgroundColor: 'var(--color-warning)' }} />
            <span>Warning</span>
          </div>
          <div className="styleguide__color-item">
            <div className="styleguide__color-swatch" style={{ backgroundColor: 'var(--color-danger)' }} />
            <span>Danger</span>
          </div>
          <div className="styleguide__color-item">
            <div className="styleguide__color-swatch" style={{ backgroundColor: 'var(--color-info)' }} />
            <span>Info</span>
          </div>
        </div>
      </section>

      {/* Buttons */}
      <section className="styleguide__section">
        <h2 className="styleguide__section-title">Buttons</h2>

        <div className="styleguide__group">
          <h3 className="styleguide__group-title">Variants</h3>
          <div className="styleguide__row">
            <Button variant="primary">Primary</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="danger">Danger</Button>
          </div>
        </div>

        <div className="styleguide__group">
          <h3 className="styleguide__group-title">Sizes</h3>
          <div className="styleguide__row">
            <Button size="sm">Small</Button>
            <Button size="md">Medium</Button>
            <Button size="lg">Large</Button>
          </div>
        </div>

        <div className="styleguide__group">
          <h3 className="styleguide__group-title">States</h3>
          <div className="styleguide__row">
            <Button loading>Loading</Button>
            <Button disabled>Disabled</Button>
          </div>
        </div>

        <div className="styleguide__group">
          <h3 className="styleguide__group-title">Icon Buttons</h3>
          <div className="styleguide__row">
            <IconButton
              variant="default"
              size="sm"
              icon={<PlusIcon />}
              aria-label="Add"
            />
            <IconButton
              variant="primary"
              size="md"
              icon={<PlusIcon />}
              aria-label="Add"
            />
            <IconButton
              variant="danger"
              size="lg"
              icon={<TrashIcon />}
              aria-label="Delete"
            />
          </div>
        </div>
      </section>

      {/* Cards */}
      <section className="styleguide__section">
        <h2 className="styleguide__section-title">Cards</h2>
        <div className="styleguide__grid">
          <Card padding="md">
            <p>Basic card with medium padding</p>
          </Card>
          <Card
            header={<CardHeader title="Card Title" subtitle="Card subtitle" />}
            padding="md"
          >
            <p>Card with header component</p>
          </Card>
          <Card variant="clickable" padding="md" onClick={() => alert('Card clicked!')}>
            <p>Clickable card - hover to see effect</p>
          </Card>
        </div>
      </section>

      {/* Badges */}
      <section className="styleguide__section">
        <h2 className="styleguide__section-title">Badges</h2>
        <div className="styleguide__row">
          <Badge status="draft">Draft</Badge>
          <Badge status="sent">Sent</Badge>
          <Badge status="paid">Paid</Badge>
          <Badge status="overdue">Overdue</Badge>
          <Badge status="void">Void</Badge>
          <Badge status="default">Default</Badge>
        </div>
      </section>

      {/* Form Inputs */}
      <section className="styleguide__section">
        <h2 className="styleguide__section-title">Form Inputs</h2>

        <div className="styleguide__form-grid">
          <Input
            label="Email"
            type="email"
            placeholder="Enter your email"
            fullWidth
          />
          <Input
            label="Password"
            type="password"
            placeholder="Enter password"
            required
            fullWidth
          />
          <Input
            label="With Error"
            type="text"
            error="This field is required"
            fullWidth
          />
          <Input
            label="Disabled"
            type="text"
            disabled
            value="Disabled input"
            fullWidth
          />
          <Textarea
            label="Message"
            placeholder="Enter your message"
            helpText="Maximum 500 characters"
            fullWidth
          />
          <Select
            label="Country"
            placeholder="Select a country"
            options={[
              { value: 'us', label: 'United States' },
              { value: 'uk', label: 'United Kingdom' },
              { value: 'ca', label: 'Canada' },
            ]}
            fullWidth
          />
        </div>

        <div className="styleguide__group">
          <h3 className="styleguide__group-title">Density</h3>
          <div className="styleguide__form-grid">
            <Input label="Compact Input" placeholder="Compact" density="compact" fullWidth />
            <Input label="Default Input" placeholder="Default" density="default" fullWidth />
            <Input label="Comfortable Input" placeholder="Comfortable" density="comfortable" fullWidth />
            <Select
              label="Compact Select"
              density="compact"
              options={[
                { value: 'one', label: 'One' },
                { value: 'two', label: 'Two' },
              ]}
              fullWidth
            />
            <Select
              label="Comfortable Select"
              density="comfortable"
              options={[
                { value: 'one', label: 'One' },
                { value: 'two', label: 'Two' },
              ]}
              fullWidth
            />
            <Textarea label="Compact Textarea" placeholder="Compact body" density="compact" fullWidth />
          </div>
        </div>

        <div className="styleguide__group">
          <h3 className="styleguide__group-title">Search Input</h3>
          <div className="styleguide__form-grid">
            <SearchInput
              placeholder="Compact Search"
              onClear={() => console.log('cleared')}
              density="compact"
              fullWidth
            />
            <SearchInput
              placeholder="Default Search"
              onClear={() => console.log('cleared')}
              fullWidth
            />
            <SearchInput
              placeholder="Comfortable Search"
              onClear={() => console.log('cleared')}
              density="comfortable"
              fullWidth
            />
          </div>
        </div>
      </section>

      <section className="styleguide__section">
        <h2 className="styleguide__section-title">Form Layout</h2>
        <div className="styleguide__group">
          <h3 className="styleguide__group-title">Form Grid Width Presets</h3>
          <FormSection
            title="Compact Form Section"
            description="Shared field-width and density controls."
            density="compact"
            actions={<Button size="sm">Save Draft</Button>}
          >
            <div className="styleguide__section-body">
              <FormGrid density="compact">
                <FormGridItem width="short">
                  <Input label="Task Number" density="compact" placeholder="T100" fullWidth />
                </FormGridItem>
                <FormGridItem width="long">
                  <Input label="Task Name" density="compact" placeholder="Retaining wall review" fullWidth />
                </FormGridItem>
                <FormGridItem width="date">
                  <Input label="Start Date" type="date" density="compact" fullWidth />
                </FormGridItem>
                <FormGridItem width="date">
                  <Input label="Due Date" type="date" density="compact" fullWidth />
                </FormGridItem>
                <FormGridItem width="full">
                  <Textarea label="Description" density="compact" placeholder="Describe the scope." fullWidth />
                </FormGridItem>
              </FormGrid>
            </div>
          </FormSection>
        </div>
      </section>

      {/* Tabs */}
      <section className="styleguide__section">
        <h2 className="styleguide__section-title">Tabs</h2>
        <Tabs
          tabs={[
            { id: 'tab1', label: 'Overview', content: <p>Overview content here</p> },
            { id: 'tab2', label: 'Details', content: <p>Details content here</p> },
            { id: 'tab3', label: 'Settings', content: <p>Settings content here</p> },
            { id: 'tab4', label: 'Disabled', content: <p>This tab is disabled</p>, disabled: true },
          ]}
        />
      </section>

      {/* Divider */}
      <section className="styleguide__section">
        <h2 className="styleguide__section-title">Dividers</h2>
        <Divider />
        <Divider label="OR" />
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span>Left</span>
          <Divider orientation="vertical" />
          <span>Right</span>
        </div>
      </section>

      {/* Skeleton */}
      <section className="styleguide__section">
        <h2 className="styleguide__section-title">Skeleton Loaders</h2>
        <div className="styleguide__skeleton-demo">
          <Skeleton variant="text" width="100%" />
          <Skeleton variant="text" width="80%" />
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="circular" />
          <Skeleton variant="rectangular" width="100%" height="200px" />
        </div>
      </section>

      {/* Empty State */}
      <section className="styleguide__section">
        <h2 className="styleguide__section-title">Empty State</h2>
        <Card padding="none">
          <EmptyState
            icon={<InboxIcon />}
            title="No items found"
            description="Get started by creating your first item"
            action={<Button variant="primary">Create Item</Button>}
          />
        </Card>
      </section>

      {/* Modal & Drawer */}
      <section className="styleguide__section">
        <h2 className="styleguide__section-title">Modal & Drawer</h2>
        <div className="styleguide__row">
          <Button onClick={() => setModalOpen(true)}>Open Modal</Button>
          <Button onClick={() => setDrawerOpen(true)}>Open Drawer</Button>
        </div>

        <Modal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          title="Modal Title"
          size="md"
          footer={
            <>
              <Button variant="ghost" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => setModalOpen(false)}>
                Confirm
              </Button>
            </>
          }
        >
          <p>This is the modal content. You can put any content here.</p>
        </Modal>

        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          title="Drawer Title"
          position="right"
          footer={
            <>
              <Button variant="ghost" onClick={() => setDrawerOpen(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => setDrawerOpen(false)}>
                Save
              </Button>
            </>
          }
        >
          <p>This is the drawer content. Drawers are useful for side panels.</p>
        </Drawer>
      </section>

      {/* Toast Notifications */}
      <section className="styleguide__section">
        <h2 className="styleguide__section-title">Toast Notifications</h2>
        <div className="styleguide__row">
          <Button onClick={() => addToast('success')}>Success Toast</Button>
          <Button onClick={() => addToast('error')}>Error Toast</Button>
          <Button onClick={() => addToast('warning')}>Warning Toast</Button>
          <Button onClick={() => addToast('info')}>Info Toast</Button>
        </div>
        <ToastContainer toasts={toasts} onDismiss={removeToast} />
      </section>

      {/* Data Table */}
      <section className="styleguide__section">
        <h2 className="styleguide__section-title">Data Table</h2>
        <div className="styleguide__group">
          <h3 className="styleguide__group-title">List Rows</h3>
          <Card padding="none">
            <ListRow
              density="compact"
              topLeft={<Badge status="draft">Draft</Badge>}
              topRight={<Button size="sm">Edit Row</Button>}
              title="Compact Row Card"
              subtitle="Address or subtitle"
              meta="Meta copy, timestamps, or small detail text."
            />
            <ListRow
              topLeft={<Badge status="paid">Paid</Badge>}
              topRight={<Button size="sm" variant="secondary">Open Row</Button>}
              title="Default Row Card"
              subtitle="Second line"
              meta="Another row using the default spacing."
            />
          </Card>
        </div>

        <div className="styleguide__group">
          <h3 className="styleguide__group-title">Table Density</h3>
          <div className="styleguide__table-stack">
            <DataTable
              columns={tableColumns}
              data={tableData}
              onRowClick={(row) => console.log('Row clicked:', row)}
              striped
              hoverable
              density="compact"
            />
            <DataTable
              columns={tableColumns}
              data={tableData}
              striped
              hoverable
              density="comfortable"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

// Icon components
const PlusIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M10 4V16M4 10H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const TrashIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M3 5H17M8 3H12M8 9V15M12 9V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M4 5H16V17C16 17.5523 15.5523 18 15 18H5C4.44772 18 4 17.5523 4 17V5Z" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const InboxIcon = () => (
  <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
    <path
      d="M8 24L32 8L56 24M8 24V52C8 53.1046 8.89543 54 10 54H54C55.1046 54 56 53.1046 56 52V24M8 24L18 36H28L32 44L36 36H46L56 24"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default Styleguide;
